import React from 'react';
import { HeartHandshake, History, AlertTriangle, CalendarClock, Timer, Users } from 'lucide-react';
import { ProjectGlobalData, ProjectStatus, SifaBetriebsdaten } from '../../types';
import { SifaKennzahlen } from '../../services/sifa/betreuungTypes';
import { Button } from '../Button';
import { asaPflichtig, einsatzzeitFortschritt, SIFA_ARCHIV_OPTION, SIFA_STATUS_OPTIONEN } from './sifaBetriebsdatenLogic';

interface Props {
  globalData: ProjectGlobalData;
  sifa: SifaBetriebsdaten | undefined;
  kennzahlen?: SifaKennzahlen;
  /** Der Umschalter laeuft ueber dieselbe handleStatusChange-Logik wie bei SiGeKo (Archiv-Rueckfrage beim Beenden). */
  onStatusChange: (status: ProjectStatus) => void;
  archivPruefungLaeuft: boolean;
  /** „Archiv" sieht nur, wer das Recht „Projekt archivieren“ hat — und nur aus „Beendet" heraus. */
  darfArchivieren: boolean;
  onStartDateChange: (value: string) => void;
  onReportExemptChange: (value: boolean) => void;
  onShowHistoryModal: () => void;
}

const datumLang = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('de-DE');
};

/**
 * Karte 1 des Hubs bei SiFa: „Betreuung" statt „Zeitplan & Intervall".
 *
 * Eine Betreuung hat keinen Begehungsturnus, keine Phasen und kein Ende —
 * deshalb hier nur Status (Laufend/Ruht/Beendet), Betreuungsbeginn, die
 * Kurzform der Betriebsdaten und die Kennzahlen aus den sifa_*-Tabellen
 * (useSifaHubData.kennzahlen). Geschrieben wird ausschliesslich ueber die
 * Handler von ProjectStats — die Karte kennt weder Report noch onUpdate.
 */
export const SifaBetriebsdatenKarte: React.FC<Props> = ({
  globalData, sifa, kennzahlen, onStatusChange, archivPruefungLaeuft, darfArchivieren,
  onStartDateChange, onReportExemptChange, onShowHistoryModal,
}) => {
  const fortschritt = einsatzzeitFortschritt(kennzahlen?.minutenJahr ?? 0, sifa?.einsatzzeitSollStunden);
  const jahr = new Date().getFullYear();
  const ueberfaellig = kennzahlen?.fristenUeberfaellig ?? 0;
  const bald = kennzahlen?.fristenBald ?? 0;
  const hatStammdaten = !!(sifa?.branche || sifa?.berufsgenossenschaft || sifa?.beschaeftigte || sifa?.betreuungsgruppe);
  const zeigeArchiv = globalData.status === 'finished' || (darfArchivieren && globalData.status === 'completed');
  const statusOptionen = zeigeArchiv ? [...SIFA_STATUS_OPTIONEN, SIFA_ARCHIV_OPTION] : SIFA_STATUS_OPTIONEN;

  return (
    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 h-[700px] flex flex-col">
      <div className="flex justify-between items-start mb-4 shrink-0">
        <div>
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2"><HeartHandshake className="w-5 h-5 text-teal-600" /> Betreuung</h3>
          <p className="text-xs text-slate-500 mt-1">Fachkraft für Arbeitssicherheit — unbefristet, ohne Begehungsturnus</p>
        </div>
        <Button variant="secondary" onClick={onShowHistoryModal} className="text-xs h-8 px-3" icon={<History className="w-3 h-3" />}>Historie</Button>
      </div>

      <div className="space-y-5 flex-1 overflow-y-auto custom-scrollbar pr-1">
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-2">Status der Betreuung</label>
          <div className="flex bg-slate-50 p-1.5 rounded-xl border border-slate-100 gap-1">
            {statusOptionen.map(opt => {
              const isActive = globalData.status === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => onStatusChange(opt.id)}
                  disabled={archivPruefungLaeuft}
                  className={`flex-1 py-2 text-[11px] font-bold rounded-lg transition-all border disabled:opacity-50 disabled:cursor-wait ${isActive ? 'bg-white border-teal-200 text-teal-700 shadow-sm' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
                >
                  {opt.id === 'finished' && archivPruefungLaeuft ? 'Prüfe…' : opt.label}
                </button>
              );
            })}
          </div>
          {globalData.status === 'planning' && (
            <p className="text-[11px] text-slate-400 mt-1.5">Noch in Vorbereitung — „Laufend" startet die Betreuung.</p>
          )}
          {globalData.status === 'completed' && (
            <p className="text-[11px] text-slate-400 mt-1.5">Beendet — die Buchhaltung stellt die Schlussrechnung.{darfArchivieren
              ? ' Ins Archiv geht das Projekt, sobald sie gestellt und bezahlt ist.'
              : ' Archivieren kann anschließend, wer das Recht „Projekt archivieren“ hat.'}</p>
          )}
        </div>

        <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
          <label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Betreuung seit</label>
          <input
            type="date"
            value={globalData.startDate || ''}
            onChange={(e) => onStartDateChange(e.target.value)}
            className="w-full text-xs font-bold text-slate-700 bg-white border border-slate-200 rounded px-2 py-1 outline-none"
          />
        </div>

        {hatStammdaten && (
          <div className="grid grid-cols-2 gap-2 text-[11px]">
            {sifa?.branche && <div className="bg-slate-50 rounded-lg px-2.5 py-1.5 border border-slate-100 col-span-2"><span className="text-slate-400 uppercase font-bold text-[10px] block">Branche</span><span className="font-bold text-slate-700">{sifa.branche}</span></div>}
            {sifa?.beschaeftigte !== undefined && <div className="bg-slate-50 rounded-lg px-2.5 py-1.5 border border-slate-100"><span className="text-slate-400 uppercase font-bold text-[10px] block">Beschäftigte</span><span className="font-bold text-slate-700">{sifa.beschaeftigte}</span></div>}
            {sifa?.betreuungsgruppe && <div className="bg-slate-50 rounded-lg px-2.5 py-1.5 border border-slate-100"><span className="text-slate-400 uppercase font-bold text-[10px] block">Betreuungsgruppe</span><span className="font-bold text-slate-700">{sifa.betreuungsgruppe}</span></div>}
            {sifa?.berufsgenossenschaft && <div className="bg-slate-50 rounded-lg px-2.5 py-1.5 border border-slate-100 col-span-2"><span className="text-slate-400 uppercase font-bold text-[10px] block">Berufsgenossenschaft</span><span className="font-bold text-slate-700 truncate block">{sifa.berufsgenossenschaft}{sifa.bgMitgliedsnummer ? ` · ${sifa.bgMitgliedsnummer}` : ''}</span></div>}
          </div>
        )}

        <div className="space-y-2">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block">Kennzahlen</label>

          <div className={`rounded-xl border p-3 ${ueberfaellig > 0 ? 'bg-red-50 border-red-100' : bald > 0 ? 'bg-amber-50 border-amber-100' : 'bg-emerald-50 border-emerald-100'}`}>
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase flex items-center gap-1.5 text-slate-600"><CalendarClock className="w-3.5 h-3.5" /> Fristen</span>
              <span className="text-xs font-bold text-slate-800">
                <span className={ueberfaellig > 0 ? 'text-red-700' : ''}>{ueberfaellig} überfällig</span>
                <span className="text-slate-400"> / </span>
                <span className={bald > 0 ? 'text-amber-700' : ''}>{bald} bald</span>
              </span>
            </div>
            <div className="text-[11px] text-slate-600 mt-1.5">
              {kennzahlen?.naechsteFrist
                ? <>Nächste Frist: <span className="font-bold text-slate-800">{kennzahlen.naechsteFrist.titel}</span> am <span className="font-mono font-bold">{datumLang(kennzahlen.naechsteFrist.faelligAm)}</span></>
                : <span className="italic text-slate-400">Keine offene Frist.</span>}
            </div>
          </div>

          <div className="rounded-xl border border-teal-100 bg-teal-50/60 p-3">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase flex items-center gap-1.5 text-slate-600"><Timer className="w-3.5 h-3.5" /> Einsatzzeit {jahr}</span>
              <span className="text-xs font-bold text-slate-800">
                {fortschritt.erbrachtStunden.toLocaleString('de-DE')} h erbracht
                {fortschritt.sollStunden !== null && <span className="text-slate-500 font-medium"> von {fortschritt.sollStunden.toLocaleString('de-DE')} h Soll</span>}
              </span>
            </div>
            {fortschritt.prozent !== null ? (
              <div className="w-full bg-white h-2 rounded-full overflow-hidden mt-2 border border-teal-100">
                <div className="h-full bg-teal-500 rounded-full transition-all duration-700" style={{ width: `${fortschritt.prozent}%` }} />
              </div>
            ) : (
              <div className="text-[11px] text-slate-500 mt-1.5">Kein Soll hinterlegt — Einsatzzeit unter Stammdaten → Betrieb eintragen.</div>
            )}
            <div className="text-[11px] text-slate-500 mt-1">{kennzahlen?.taetigkeitenJahr ?? 0} Tätigkeiten im Nachweis</div>
          </div>

          {asaPflichtig(sifa?.beschaeftigte) && (
            <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-3 flex items-start gap-2 text-[11px] text-indigo-900">
              <Users className="w-3.5 h-3.5 mt-0.5 shrink-0 text-indigo-600" />
              <span><span className="font-bold">ASA-Pflicht:</span> Betriebe mit mehr als 20 Beschäftigten (§ 11 ASiG) — vierteljährlich.</span>
            </div>
          )}

          {sifa?.beschaeftigte === undefined && (
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-3 flex items-start gap-2 text-[11px] text-slate-500">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-400" />
              <span>Beschäftigtenzahl fehlt — Betreuungsgruppe, Einsatzzeit-Soll und ASA-Pflicht bleiben ohne Grundlage.</span>
            </div>
          )}
        </div>

        <div className="pt-3 border-t border-slate-200">
          <label className="flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={globalData.reportExempt === true}
              onChange={(e) => onReportExemptChange(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
            />
            <span>
              <span className="text-xs font-bold text-slate-700 block">Keine Begehungen für diesen Betrieb</span>
              <span className="text-[11px] text-slate-400 block mt-0.5">Unterdrückt Begehungs-Prüfungen und -Erinnerungen (nur Leistungsbuchung).</span>
            </span>
          </label>
        </div>
      </div>
    </div>
  );
};
